import { LoadingButton } from "@mui/lab";
import { useTranslation } from "react-i18next";

const { Dialog, DialogTitle, DialogContent, Alert, TextField, Box, DialogContentText, DialogActions, Button, FormControl, InputLabel, Select, MenuItem, Typography } = require("@mui/material");
const dayjs = require("dayjs");
const PropTypes = require("prop-types");
const { useState } = require("react");

SessionSchedulingDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onSubmit: PropTypes.func,
    sessions: PropTypes.array
};
function SessionSchedulingDialog({ open, onClose, onSubmit, sessions }) {
    const [date, setDate] = useState(dayjs().format('YYYY-MM-DD'));
    const [time, setTime] = useState(dayjs().add(1, 'hour').format('HH:mm'));
    const [selectedSessionId, setSelectedSessionId] = useState(sessions[0]?.id);
    const [selectedKind, setSelectedKind] = useState('ONLINE');
    const [error, setError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const { t } = useTranslation();

    const handleSubmit = async (event) => {
        event.preventDefault();
        const scheduledAt = dayjs(`${date} ${time}`).toDate();
        if (scheduledAt < new Date()) {
            setError('Scheduled time must be in the future');
            return;
        }
        setIsSubmitting(true);
        const [_date, _time] = scheduledAt.toISOString().split(/[TZ.]/);
        onSubmit({
            date: _date, time: _time, selectedSessionId, selectedKind
        }).then(() => {
            setIsSubmitting(false);
            onClose();
        }).catch((error) => {
            setIsSubmitting(false);
            setError(error.message
                ? error.message
                : 'An error occurred. Please try again later.');
        });
    }

    const timeDifference = dayjs().to(dayjs(`${date} ${time}`));

    return (
        <Dialog
            open={isSubmitting || open}
            onClose={isSubmitting ? undefined : onClose}
            PaperProps={{
                component: 'form',
                onSubmit: handleSubmit
            }}
            maxWidth="xs"
        >
            <DialogTitle>{t("sc_session")}</DialogTitle>
            <DialogContent>
                {error && <Alert sx={{ marginBottom: "2rem" }} severity="error" onClose={() => setError(null)}>{error}</Alert>}

                <Box display="flex" flexDirection="column" gap="2rem" marginBottom="2rem" marginTop=".75rem">
                    <Box>
                        <InputLabel id="sessions-select-label">{t("session_type")}</InputLabel>
                        <Select
                            size="small"
                            variant="standard"
                            labelId="sessions-select-label"
                            id="sessions-select-id"
                            value={selectedSessionId}
                            onChange={(event) => setSelectedSessionId(event.target.value)}
                            label={t("session_type")}
                            disabled={isSubmitting}
                            fullWidth
                        >
                            {sessions.map((session) => (
                                <MenuItem key={session.id} value={session.id}>{session.name}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box>
                        <InputLabel id="kind-select-label">{t("session_kind")}</InputLabel>
                        <Select
                            size="small"
                            variant="standard"
                            labelId="kind-select-label"
                            id="kind-select-id"
                            value={selectedKind}
                            onChange={(event) => setSelectedKind(event.target.value)}
                            label={t("session_kind")}
                            disabled={isSubmitting}
                            fullWidth
                        >
                            {['ONLINE', 'IN_PERSON'].map((kind) => (
                                <MenuItem key={kind} value={kind}>{t(`ses_kind.${kind}`)}</MenuItem>
                            ))}
                        </Select>
                    </Box>
                    <Box display="flex" gap="2rem">
                        <Box sx={{ flex: 1 }}>
                            <InputLabel id="date-label">{t("Date")}</InputLabel>
                            <TextField
                                id="date"
                                type="date"
                                labelId="date-label"
                                value={date}
                                onChange={(event) => setDate(event.target.value)}
                                variant='standard'
                                disabled={isSubmitting}
                                fullWidth
                            />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <InputLabel id="time-label">{t("Time")}</InputLabel>
                            <TextField
                                id="time"
                                type="time"
                                labelId="time-label"
                                value={time}
                                onChange={(event) => setTime(event.target.value)}
                                variant='standard'
                                disabled={isSubmitting}
                                fullWidth
                            />
                        </Box>
                    </Box>
                </Box>
                <DialogContentText>
                    {t("mb_notif")}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={isSubmitting}>{t("Cancel")}</Button>
                <LoadingButton type="submit" disabled={isSubmitting} loading={isSubmitting} >{t("Schedule")}</LoadingButton>
            </DialogActions>
        </Dialog >
    );
}

export default SessionSchedulingDialog;